import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBuilding, faHotel, faShip, faTicket, IconDefinition } from '@fortawesome/free-solid-svg-icons';

import { ICertificateTemplate, ItemTypeEnum } from '@share/common-types';
import { Routes } from '@share/constants';

import { useCertificateTemplatesCatalog } from '../../../certificates/hooks';
import { getCertificateImage } from './images';

import styles from './styles.module.scss';

const TEASER_COUNT = 3;
// Module-level so the catalog hook doesn't refetch on every render. Empty = every item type.
const ALL_TYPES: ItemTypeEnum[] = [];

const ITEM_ICONS: Record<ItemTypeEnum, IconDefinition> = {
  [ItemTypeEnum.Hotel]: faHotel,
  [ItemTypeEnum.Condo]: faBuilding,
  [ItemTypeEnum.Cruise]: faShip,
};

interface IProps {
  accountUsername: string;
}

interface ITileProps {
  template: ICertificateTemplate;
  onView: () => void;
  onBuy: () => void;
}

/** A catalog certificate in the hub's style; same data and savings maths as the catalog card. */
const TeaserTile = ({ template, onView, onBuy }: ITileProps): React.ReactElement => {
  const intl = useIntl();
  const [imageFailed, setImageFailed] = useState(false);

  const items = template.itemTemplates || [];
  const money = (value: number) => intl.formatNumber(value || 0, { style: 'currency', currency: 'USD' });
  const retail = template.averageRetailPrice || 0;
  const savingsPercent = retail > 0 ? Math.round(((template.savings || 0) / retail) * 100) : 0;

  return (
    <article className={styles.teaserTile}>
      <div className={styles.teaserMedia}>
        <img
          src={getCertificateImage(template.imageUrl, items[0]?.type, imageFailed)}
          alt=""
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
        {savingsPercent > 0 ? (
          <span className={styles.teaserBadge}>
            {intl.formatMessage({ id: 'my.account.hub.certificates.teaser_save' }, { percent: savingsPercent })}
          </span>
        ) : null}
      </div>

      <div className={styles.teaserTileBody}>
        <div className={styles.teaserTileTop}>
          <h3 className={styles.teaserName}>{template.name}</h3>
          <span className={styles.teaserPrice}>{money(template.totalPrice)}</span>
        </div>

        {items.length ? (
          <ul className={styles.chips}>
            {items.map((item, index) => (
              <li key={`${item.type}-${index}`} className={styles.chip}>
                <FontAwesomeIcon icon={ITEM_ICONS[item.type] || faTicket} aria-hidden="true" />
                {intl.formatMessage({ id: 'certificates.card.tag.nights' }, { type: item.type, nights: item.nights })}
              </li>
            ))}
          </ul>
        ) : null}

        {template.savings > 0 ? (
          <p className={styles.teaserSavings}>
            {intl.formatMessage(
              { id: 'my.account.hub.certificates.teaser_savings' },
              { retail: money(retail), savings: money(template.savings) },
            )}
          </p>
        ) : null}

        {template.validityMonths > 0 ? (
          <p className={styles.meta}>
            {intl.formatMessage({ id: 'my.account.hub.certificates.teaser_validity' }, { months: template.validityMonths })}
          </p>
        ) : null}

        <div className={styles.teaserActions}>
          <button type="button" className={styles.toggleButton} onClick={onBuy}>
            {intl.formatMessage({ id: 'my.account.hub.certificates.teaser_buy' })}
          </button>
          <button type="button" className={styles.linkButton} onClick={onView}>
            {intl.formatMessage({ id: 'certificates.wallet.seeDetails' })}
          </button>
        </div>
      </div>
    </article>
  );
};

/**
 * A few catalog certificates at the bottom of My certificates, drawn in the hub's style. Viewing and
 * buying go to the existing catalog pages and purchase flow; the certificates module is untouched.
 */
export const CertificatesCatalogTeaser = ({ accountUsername }: IProps): React.ReactElement | null => {
  const intl = useIntl();
  const history = useHistory();

  const { items, loading, error } = useCertificateTemplatesCatalog({
    pageSize: TEASER_COUNT,
    itemTypes: ALL_TYPES,
    sortOption: 'priceAsc',
  });

  if (error || (!loading && !items.length)) {
    return null;
  }

  const catalogPath = `/${accountUsername}${Routes.CertificatesCatalog}`;

  return (
    <section className={styles.teaser} aria-labelledby="account-hub-catalog-teaser-title">
      <div className={styles.teaserHeader}>
        <div>
          <h2 id="account-hub-catalog-teaser-title" className={styles.teaserTitle}>
            {intl.formatMessage({ id: 'my.account.hub.certificates.teaser_title' })}
          </h2>
          <p className={styles.teaserBody}>{intl.formatMessage({ id: 'my.account.hub.certificates.teaser_body' })}</p>
        </div>
        <Link to={catalogPath} className={styles.teaserLink}>
          {intl.formatMessage({ id: 'my.account.hub.certificates.teaser_all' })}
          <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
        </Link>
      </div>
      <div className={styles.teaserGrid}>
        {loading
          ? Array.from({ length: TEASER_COUNT }, (_, index) => (
            <div key={index} className={styles.teaserTile}>
              <div className={styles.teaserMedia} />
              <div className={styles.teaserTileBody}><Skeleton active paragraph={{ rows: 3 }} /></div>
            </div>
          ))
          : items.slice(0, TEASER_COUNT).map(template => (
            <TeaserTile
              key={template.id}
              template={template}
              onView={() => history.push(`/${accountUsername}${Routes.CertificateTemplateDetails}/${template.id}`)}
              onBuy={() => history.push(`/${accountUsername}${Routes.Certificates}/${template.id}/payment`)}
            />
          ))}
      </div>
    </section>
  );
};
